import React from 'react';
import { Helmet } from 'react-helmet';
import { Building2, Shield, FileText, TrendingUp, Users, CheckCircle, Award, Clock, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-blue-600" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const CompanyFormation = ({ onOpenPopup }) => {
  const [statsRef, statsVisible] = useScrollAnimation(0.2);
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);

  const faqs = [
    {
      id: 1,
      question: "Which business structure is right for me?",
      answer: "The best structure depends on your business goals, number of owners, desired liability protection, and tax situation. LLCs are popular for small businesses due to flexibility and protection. Corporations are better for raising capital and planning significant growth. We provide free consultations to help you choose."
    },
    {
      id: 2,
      question: "How long does company formation take?",
      answer: "Timeline varies by state and entity type. Most LLC formations take 1-3 weeks, while corporations may take 2-4 weeks. We handle all paperwork and expedite when possible to get your business running quickly."
    },
    {
      id: 3,
      question: "What's included in your formation service?",
      answer: "Our service includes entity selection consultation, document preparation, state filing, EIN acquisition, customized operating agreement or bylaws, and compliance guidance. We handle everything from start to finish."
    },
    {
      id: 4,
      question: "Do I need an operating agreement?",
      answer: "Yes! Even single-member LLCs benefit from operating agreements. They establish ownership, management structure, and procedures for important decisions. This document protects your personal assets and prevents disputes."
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Company Formation Services | LLC & Corporation Registration</title>
        <meta name="description" content="Professional company formation services for LLCs, corporations, and other business entities. Fast, affordable business registration with expert guidance and compliance support." />
        <meta name="keywords" content="company formation, LLC registration, incorporate business, business entity formation, start LLC, corporate registration, business formation services" />
      </Helmet>

      {/* Creative Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 text-white py-32 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float">
            <Building2 className="w-20 h-20 text-white/10" />
          </div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}>
            <Shield className="w-24 h-24 text-white/10" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">🏢 Business Formation</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Company Formation</h1>
            <p className="text-xl mb-8 text-white/95 leading-relaxed max-w-3xl mx-auto animate-slide-in">
              Launch your business with confidence through our streamlined company formation services and expert legal guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
              <Button
                onClick={onOpenPopup}
                className="bg-white text-blue-600 hover:bg-gray-100 h-12 px-8 text-base hover:scale-105 transition-all"
              >
                Get Started Today
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base"
                onClick={() => document.getElementById('entity-types').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div 
            ref={statsRef}
            className={`grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto stagger-children ${statsVisible ? 'visible' : ''}`}
          >
            <StatCard icon={Users} value="5000" label="Companies Formed" delay={100} />
            <StatCard icon={Award} value="99" label="Success Rate %" delay={200} />
            <StatCard icon={Clock} value="2" label="Week Avg Formation" delay={300} />
            <StatCard icon={DollarSign} value="50" label="States Covered" delay={400} />
          </div>
        </div>
      </section>

      {/* Overview with Image */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHN0YXJ0dXB8ZW58MHx8fHwxNzYyNTQ4OTg5fDA&ixlib=rb-4.1.0&q=85?w=800&h=600&fit=crop" 
                      alt="Business startup" 
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent flex items-end p-8">
                      <div className="text-white">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                            <Building2 size={32} />
                          </div>
                          <div>
                            <div className="text-2xl font-bold">Legal Protection</div>
                            <div className="text-white/90">Asset Security</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  📚 Learn More
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Start Your Business Right</h2>
                <div className="space-y-4 text-base text-gray-700 leading-relaxed">
                  <p>
                    Forming a business entity is one of the most important decisions you'll make as an entrepreneur. The right business structure protects your personal assets, provides tax benefits, and establishes credibility with customers and partners.
                  </p>
                  <p>
                    Our company formation services handle all the legal paperwork and filing requirements, so you can focus on building your business. We guide you through entity selection, prepare all necessary documents, and ensure compliance with state and federal regulations.
                  </p>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border-l-4 border-blue-600">
                    <p className="font-semibold text-gray-900 mb-2">💡 Did You Know?</p>
                    <p className="text-gray-700">
                      Choosing the right business structure can save you thousands in taxes annually and protect your personal assets from business liabilities.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Entity Types */}
      <section id="entity-types" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Business Entity Types</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the structure that best fits your business goals and growth plans
            </p>
          </div>
          <div 
            ref={benefitsRef}
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto stagger-children ${benefitsVisible ? 'visible' : ''}`}
          >
            <div className="group relative bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">LLC Formation</h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                Limited Liability Company (LLC) offers flexibility, liability protection, and pass-through taxation. Ideal for small to medium businesses.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Personal asset protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Flexible management structure</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Pass-through taxation</span>
                </li>
              </ul>
            </div>

            <div className="group relative bg-gradient-to-br from-orange-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-orange-100">
              <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingUp className="text-orange-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Corporation</h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                C-Corporation and S-Corporation structures for businesses seeking investment or planning significant growth.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Strong liability protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Easier to raise capital</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Perpetual existence</span>
                </li>
              </ul>
            </div>

            <div className="group relative bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all border border-blue-100">
              <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="text-blue-600" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">DBA Registration</h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                "Doing Business As" registration allows you to operate under a trade name different from your legal business name.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Professional branding</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Multiple business names</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-primary flex-shrink-0 mt-0.5" size={18} />
                  <span>Quick and affordable</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Images Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group h-96">
                <img 
                  src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHN0YXJ0dXB8ZW58MHx8fHwxNzYyNTQ4OTg5fDA&ixlib=rb-4.1.0&q=85?w=600&h=400&fit=crop" 
                  alt="Business team" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-900/40 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                      <h3 className="text-2xl font-bold text-white mb-2">Launch with Confidence</h3>
                      <p className="text-white/90">Expert guidance through every step of business formation</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group h-96">
                <img 
                  src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw2fHxidXNpbmVzcyUyMHBsYW5uaW5nfGVufDB8fHx8MTc2MjU0ODk4OXww&ixlib=rb-4.1.0&q=85?w=600&h=400&fit=crop" 
                  alt="Business planning" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/80 via-orange-900/40 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                      <h3 className="text-2xl font-bold text-white mb-2">Complete Support</h3>
                      <p className="text-white/90">From entity selection to compliance - we handle everything</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What's Included</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Entity Selection Consultation</h3>
                <p className="text-gray-600 text-sm">Expert guidance on choosing the right business structure</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Document Preparation</h3>
                <p className="text-gray-600 text-sm">Complete preparation of formation documents and filings</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">State Filing</h3>
                <p className="text-gray-600 text-sm">File all required documents with the appropriate state agency</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">EIN Acquisition</h3>
                <p className="text-gray-600 text-sm">Obtain your federal Employer Identification Number</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Operating Agreement</h3>
                <p className="text-gray-600 text-sm">Customized operating agreement for your business</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="bg-primary/10 w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-primary" size={20} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Compliance Guidance</h3>
                <p className="text-gray-600 text-sm">Ongoing support for annual filings and compliance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs - Creative Design */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ❓ Got Questions?
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about company formation
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-2xl px-6 border-none shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-primary py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-gray-600 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSlider />

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse-slow"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Business?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Let us handle the paperwork while you focus on your business vision
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-white text-blue-600 hover:bg-gray-100 h-14 px-10 text-lg hover:scale-105 transition-all shadow-xl"
          >
            Start Your Formation Today
          </Button>
        </div>
      </section>
    </div>
  );
};